import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  CircleDot,
  User,
  DollarSign,
  Calendar,
  Plus,
  Package,
  ArrowRight,
} from "lucide-react";
import {
  useCreateOpportunity,
  usePipelineStages,
  usePipelineProducts,
  usePipelinePersonas,
  REVENUE_REGULARITIES,
  PipelineOpportunity,
} from "@/hooks/usePipeline";
import { OpportunityModal } from "@/components/pipeline/OpportunityModal";
import { AccountContactPicker } from "@/components/pipeline/AccountContactPicker";
import { toast } from "sonner";

interface AccountPipelineOpsTabProps {
  accountId: string;
  companyName?: string;
  companyDomain?: string;
}

export function AccountPipelineOpsTab({
  accountId,
  companyName,
  companyDomain,
}: AccountPipelineOpsTabProps) {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState<PipelineOpportunity | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    stage_id: "",
    product_id: "",
    owner_id: "",
    deal_value: "",
    revenue_regularity: "",
    expected_close_date: "",
    next_action: "",
    next_action_date: "",
    opp_summary: "",
    notes: "",
    main_contact_name: "",
    main_contact_email: "",
    main_contact_linkedin: "",
  });

  // Queries
  const { data: opportunities, isLoading } = useQuery({
    queryKey: ["account-pipeline", accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pipeline_board" as any)
        .select("*")
        .eq("account_id", accountId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as PipelineOpportunity[];
    },
  });

  const { data: stages } = usePipelineStages();
  const { data: products } = usePipelineProducts();
  const { data: personas } = usePipelinePersonas();
  const createOpportunity = useCreateOpportunity();

  const resetForm = () => {
    setFormData({
      name: "",
      stage_id: "",
      product_id: "",
      owner_id: "",
      deal_value: "",
      revenue_regularity: "",
      expected_close_date: "",
      next_action: "",
      next_action_date: "",
      opp_summary: "",
      notes: "",
      main_contact_name: "",
      main_contact_email: "",
      main_contact_linkedin: "",
    });
  };

  const openCreateForm = () => {
    resetForm();
    setFormData((prev) => ({
      ...prev,
      name: companyName ? `${companyName} — ` : "",
    }));
    setIsCreateOpen(true);
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error("Please enter an opportunity name");
      return;
    }
    const defaultStage = stages?.[0]?.id || "";

    createOpportunity.mutate(
      {
        name: formData.name.trim(),
        stage_id: formData.stage_id || defaultStage,
        product_id: formData.product_id || null,
        owner_id: formData.owner_id || null,
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null,
        revenue_regularity: formData.revenue_regularity || null,
        expected_close_date: formData.expected_close_date || null,
        next_action: formData.next_action || null,
        next_action_date: formData.next_action_date || null,
        opp_summary: formData.opp_summary || null,
        notes: formData.notes || null,
        main_contact_name: formData.main_contact_name || null,
        main_contact_email: formData.main_contact_email || null,
        main_contact_linkedin: formData.main_contact_linkedin || null,
        account_id: accountId,
        company_domain: companyDomain || null,
      },
      {
        onSuccess: () => {
          toast.success("Opportunity created");
          setIsCreateOpen(false);
          resetForm();
          queryClient.invalidateQueries({ queryKey: ["account-pipeline", accountId] });
        },
        onError: () => toast.error("Failed to create opportunity"),
      }
    );
  };

  const handleRefetch = () => {
    queryClient.invalidateQueries({ queryKey: ["account-pipeline", accountId] });
    queryClient.invalidateQueries({ queryKey: ["pipeline-board"] });
  };

  return (
    <div>
      {/* Header with create button */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h3 className="text-sm font-semibold">Pipeline Opportunities</h3>
          <p className="text-xs text-muted-foreground">
            {opportunities?.length || 0} opportunit{opportunities?.length === 1 ? "y" : "ies"} linked to this account
          </p>
        </div>
        <Button size="sm" onClick={openCreateForm}>
          <Plus className="h-4 w-4 mr-1" />
          New Opportunity
        </Button>
      </div>

      {/* Opportunity list */}
      {isLoading ? (
        <div className="flex items-center justify-center h-[200px]">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !opportunities?.length ? (
        <div className="p-8 text-center">
          <CircleDot className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="text-base font-medium mb-1">No Pipeline Opportunities</h3>
          <p className="text-muted-foreground text-sm mb-4">
            No opportunities linked to this account yet
          </p>
          <Button variant="outline" size="sm" onClick={openCreateForm}>
            <Plus className="h-4 w-4 mr-1" />
            Create First Opportunity
          </Button>
        </div>
      ) : (
        <div className="divide-y">
          {opportunities.map((opp) => (
            <div
              key={opp.id}
              className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => {
                setSelectedOpp(opp);
                setIsModalOpen(true);
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{opp.name}</span>
                    <Badge
                      variant="outline"
                      className="text-xs"
                      style={{
                        backgroundColor: `${opp.stage_color}20`,
                        color: opp.stage_color,
                        borderColor: `${opp.stage_color}40`,
                      }}
                    >
                      {opp.stage_name}
                    </Badge>
                    {opp.product_name && (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Package className="h-3 w-3" />
                        {opp.product_name}
                      </Badge>
                    )}
                  </div>
                  {opp.opp_summary && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {opp.opp_summary}
                    </p>
                  )}
                  {opp.next_action && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                      <ArrowRight className="h-3 w-3" />
                      <span className="line-clamp-1">{opp.next_action}</span>
                      {opp.next_action_date && (
                        <span className="font-medium ml-1">
                          • {new Date(opp.next_action_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    {opp.owner_name && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {opp.owner_name}
                      </span>
                    )}
                    {opp.deal_value && (
                      <span className="flex items-center gap-1 text-emerald-600 font-medium">
                        <DollarSign className="h-3 w-3" />$
                        {Number(opp.deal_value).toLocaleString()}
                      </span>
                    )}
                    {opp.expected_close_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Close: {new Date(opp.expected_close_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Opportunity Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              New Opportunity{companyName ? ` — ${companyName}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Opportunity Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ACME — Launch Platform"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Stage</Label>
                <Select
                  value={formData.stage_id}
                  onValueChange={(v) => setFormData({ ...formData, stage_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages?.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} ({s.probability}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Product</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(v) => setFormData({ ...formData, product_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Rep / Owner</Label>
              <Select
                value={formData.owner_id}
                onValueChange={(v) => setFormData({ ...formData, owner_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Assign rep" />
                </SelectTrigger>
                <SelectContent>
                  {personas?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.full_name}
                      {p.title ? ` — ${p.title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Main Contact — picker + manual fallback */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">Main Contact</h4>
                <AccountContactPicker
                  accountId={accountId}
                  onSelect={(contact) => {
                    setFormData((prev) => ({
                      ...prev,
                      main_contact_name: contact.full_name || "",
                      main_contact_email: contact.email || "",
                      main_contact_linkedin: contact.linkedin_url || "",
                    }));
                  }}
                />
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={formData.main_contact_name}
                    onChange={(e) => setFormData({ ...formData, main_contact_name: e.target.value })}
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.main_contact_email}
                      onChange={(e) => setFormData({ ...formData, main_contact_email: e.target.value })}
                      placeholder="john@acme.com"
                    />
                  </div>
                  <div>
                    <Label>LinkedIn</Label>
                    <Input
                      value={formData.main_contact_linkedin}
                      onChange={(e) => setFormData({ ...formData, main_contact_linkedin: e.target.value })}
                      placeholder="linkedin.com/in/..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Deal Value</Label>
                <Input
                  type="number"
                  value={formData.deal_value}
                  onChange={(e) =>
                    setFormData({ ...formData, deal_value: e.target.value })
                  }
                  placeholder="10000"
                />
              </div>
              <div>
                <Label>Revenue Type</Label>
                <Select
                  value={formData.revenue_regularity}
                  onValueChange={(v) =>
                    setFormData({ ...formData, revenue_regularity: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVENUE_REGULARITIES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.icon} {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Expected Close Date</Label>
              <Input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) =>
                  setFormData({ ...formData, expected_close_date: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Next Action</Label>
                <Input
                  value={formData.next_action}
                  onChange={(e) =>
                    setFormData({ ...formData, next_action: e.target.value })
                  }
                  placeholder="Schedule discovery call"
                />
              </div>
              <div>
                <Label>Next Action Date</Label>
                <Input
                  type="date"
                  value={formData.next_action_date}
                  onChange={(e) =>
                    setFormData({ ...formData, next_action_date: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <Label>Summary</Label>
              <Textarea
                value={formData.opp_summary}
                onChange={(e) =>
                  setFormData({ ...formData, opp_summary: e.target.value })
                }
                placeholder="Brief summary..."
                rows={2}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <Button
              onClick={handleCreate}
              disabled={createOpportunity.isPending}
              className="w-full"
            >
              {createOpportunity.isPending ? "Creating..." : "Create Opportunity"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Opportunity Detail Modal */}
      <OpportunityModal
        opportunity={selectedOpp}
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setSelectedOpp(null);
        }}
        stages={stages || []}
        products={products || []}
        personas={personas || []}
        onRefetch={handleRefetch}
      />
    </div>
  );
}
